import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faTrash,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import {
  Button,
  Modal,
  Form,
  Image
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import config from '../config/config';
import { useResto } from '../contexts/RestoContext';
import { useMode } from '../contexts/ModeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ucfirst } from '../utils/utils';

function PressItem({
  press,
  handleDelete,
  handleRankingUpdate,
  handleEdit,
  isFirst,
  isLast
}) {
  const { t } = useTranslation();
  const { isModeAdmin } = useMode();
  const { restoData } = useResto();
  const { activeLanguage } = useLanguage();

  const availableLanguages =
    restoData?.availableLanguages || [];

  const [showConfirmation, setShowConfirmation] =
    useState(false);
  const [showEditModal, setShowEditModal] =
    useState(false);

  const [editedPress, setEditedPress] =
    useState({ ...press });

  /* ================= SYNC WHEN PRESS CHANGES ================= */

  useEffect(() => {
    setEditedPress({ ...press });
  }, [press]);

  /* ================= DELETE ================= */

  const handleTrashClick = () =>
    setShowConfirmation(true);

  const handleCloseConfirmation = () =>
    setShowConfirmation(false);

  const handleConfirmDelete = () => {
    handleDelete(press.id);
    setShowConfirmation(false);
  };

  /* ================= RANKING ================= */

  const handleRanking = (direction) => {
    handleRankingUpdate(direction, press.id);
  };

  /* ================= EDIT ================= */

  const handleEditClick = () => {
    setEditedPress({ ...press });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () =>
    setShowEditModal(false);

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;

    setEditedPress(prev => ({
      ...prev,
      [name]: value || ''
    }));
  };

  const handleEditSubmit = () => {
    handleEdit(press.id, editedPress);
    setShowEditModal(false);
  };

  /* ================= LINK ================= */

  const originalLinkText = press?.url || '';
  const truncatedLinkText =
    originalLinkText.length > 35
      ? `${originalLinkText.substring(0, 32)}...`
      : originalLinkText;

  /* ================= RENDER ================= */

  return (
    <div
      className="kubuni-frame"
      style={{ paddingBottom: '20px' }}
    >

      {/* ================= ADMIN MODE ================= */}

      {isModeAdmin && (
        <>
          <div style={{ fontWeight: 'bold' }}>
            {press?.label}
          </div>

          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '20px'
            }}
          >
            <a
              href={press?.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgb(0,0,0)' }}
            >
              {truncatedLinkText}
            </a>
          </div>

          {availableLanguages.map(language => (
            <div key={language}>
              <div>
                <Image
                  src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                  className="flag-label"
                  alt={language}
                />
                {' '}
                {press?.[
                  `description${ucfirst(language)}`
                ]}
              </div>
            </div>
          ))}

          {/* ACTION BUTTONS */}

          <div style={{ marginTop: '10px' }}>
            <Button
              variant="primary"
              className="btn-sm"
              style={{ margin: '0 5px' }}
              onClick={handleEditClick}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>

            {!isFirst && (
              <Button
                variant="primary"
                className="btn-sm"
                style={{ margin: '0 5px' }}
                onClick={() =>
                  handleRanking('up')
                }
              >
                <FontAwesomeIcon icon={faAngleUp} />
              </Button>
            )}

            {!isLast && (
              <Button
                variant="primary"
                className="btn-sm"
                style={{ margin: '0 5px' }}
                onClick={() =>
                  handleRanking('down')
                }
              >
                <FontAwesomeIcon icon={faAngleDown} />
              </Button>
            )}

            <Button
              variant="primary"
              className="btn-sm"
              style={{ margin: '0 5px' }}
              onClick={handleTrashClick}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>

          {/* DELETE MODAL */}

          <Modal
            show={showConfirmation}
            onHide={handleCloseConfirmation}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {t('CONFIRM_DELETE')}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <p>
                {t('CONFIRM_DELETE_MESSAGE')}
              </p>
            </Modal.Body>

            <Modal.Footer
              className="justify-content-center"
            >
              <Button
                variant="secondary"
                onClick={handleCloseConfirmation}
              >
                {t('CANCEL')}
              </Button>

              <Button
                variant="primary"
                onClick={handleConfirmDelete}
              >
                {t('CONFIRM')}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* EDIT MODAL */}

          <Modal
            show={showEditModal}
            onHide={handleCloseEditModal}
            size="xl"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {t('EDIT')}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form>

                {/* TITLE */}

                <Form.Group>
                  <Form.Label>
                    {t('TITLE')}
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="label"
                    value={
                      editedPress?.label || ''
                    }
                    onChange={
                      handleEditInputChange
                    }
                  />
                </Form.Group>

                {/* URL */}

                <Form.Group className="mt-3">
                  <Form.Label>
                    {t('URL')}
                  </Form.Label>

                  <Form.Control
                    type="url"
                    name="url"
                    value={
                      editedPress?.url || ''
                    }
                    onChange={
                      handleEditInputChange
                    }
                  />
                </Form.Group>

                {/* DESCRIPTIONS */}

                {availableLanguages.map(language => {
                  const key =
                    `description${ucfirst(language)}`;

                  return (
                    <Form.Group
                      className="mt-3"
                      key={language}
                    >
                      <Form.Label>
                        Description{' '}
                        <Image
                          src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                          className="flag-label"
                          alt={language}
                        />
                      </Form.Label>

                      <Form.Control
                        as="textarea"
                        rows={3}
                        name={key}
                        value={
                          editedPress?.[key] ||
                          ''
                        }
                        onChange={
                          handleEditInputChange
                        }
                      />
                    </Form.Group>
                  );
                })}

              </Form>
            </Modal.Body>

            <Modal.Footer
              className="justify-content-center"
            >
              <Button
                variant="secondary"
                onClick={handleCloseEditModal}
              >
                {t('CANCEL')}
              </Button>

              <Button
                variant="primary"
                onClick={handleEditSubmit}
              >
                {t('CONFIRM')}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      {/* ================= USER MODE ================= */}

      {!isModeAdmin && (
        <div>

          <div style={{ fontWeight: 'bold' }}>
            {press?.label}
          </div>

          <div style={{ fontWeight: 'bold' }}>
            <a
              href={press?.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgb(0,0,0)' }}
            >
              {truncatedLinkText}
            </a>
          </div>

          <div>
            {
              press?.[
                `description${ucfirst(activeLanguage)}`
              ]
            }
          </div>

        </div>
      )}

    </div>
  );
}

export default PressItem;